<template>
  <div>
    <v-row>
      <v-col cols="12" sm="12" md="1"></v-col>
      <v-col cols="12" sm="12" md="10">
        <v-card class="mx-auto my-5 mb-16" max-width="1100">
          <v-app-bar flat :color="typeColor" dark>
            <span class="headline">{{ title }}</span>
          </v-app-bar>

          <v-card-text>
            <v-container>
              <!-- Type selector -->
              <div class="subtitle-1 mb-2">
                {{ $t("betriebsanweisung.type") }}
              </div>
              <div class="d-flex flex-wrap mb-4" style="gap: 10px">
                <v-card
                  v-for="t in baTypes"
                  :key="t.value"
                  outlined
                  class="pa-3 type-card"
                  :style="typeCardStyle(t)"
                  @click="onTypeChange(t.value)"
                >
                  <v-icon :color="t.color" class="mr-1">
                    {{ typeIcon(t.value) }}
                  </v-icon>
                  {{ $t(t.labelKey) }}
                </v-card>
              </div>

              <v-btn
                v-if="!isTemplate"
                outlined
                color="primary"
                class="mb-4"
                @click="openTemplateChooser"
              >
                <v-icon left>mdi-file-document-outline</v-icon>
                {{ $t("betriebsanweisung.useTemplate") }}
              </v-btn>

              <!-- Header fields -->
              <v-row>
                <v-col cols="12" :md="isTemplate ? (isMaster ? 8 : 12) : 6">
                  <v-text-field
                    v-model="form.title"
                    :label="titleFieldLabel"
                    :rules="[(v) => !!v || $t('general.fieldNeedsToBeFilled')]"
                    outlined
                    dense
                  />
                </v-col>
                <!-- Scope (global/local) - only the master may set global -->
                <v-col v-if="isTemplate && isMaster" cols="12" md="4">
                  <v-select
                    v-model="form.scope"
                    :items="scopeItems"
                    :label="$t('betriebsanweisung.scope')"
                    outlined
                    dense
                  />
                </v-col>
                <v-col v-if="!isTemplate" cols="12" md="6">
                  <v-text-field
                    v-model="form.number"
                    :label="$t('betriebsanweisung.number')"
                    outlined
                    dense
                  />
                </v-col>
              </v-row>
              <template v-if="!isTemplate">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.company"
                      :label="$t('betriebsanweisung.company')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.workplace"
                      :label="$t('betriebsanweisung.workplace')"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.responsiblePerson"
                      :label="$t('betriebsanweisung.responsible')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="6" md="3">
                    <v-combobox
                      v-model="form.tag"
                      :items="labelTagNames"
                      :label="$t('betriebsanweisung.tag')"
                      outlined
                      dense
                      clearable
                      hide-selected
                      @change="onTagChange"
                    >
                      <template #item="{ item }">
                        <div class="d-flex align-center">
                          <span :style="dotStyle(colorForTag(item))"></span
                          >{{ item }}
                        </div>
                      </template>
                    </v-combobox>
                  </v-col>
                  <v-col cols="6" md="3">
                    <v-select
                      v-model="form.color"
                      :items="colorOptions"
                      :label="$t('betriebsanweisung.color')"
                      item-text="label"
                      item-value="value"
                      outlined
                      dense
                      clearable
                      @change="form.labelId = null"
                    >
                      <template #selection="{ item }">
                        <div class="d-flex align-center">
                          <span :style="dotStyle(item.value)"></span
                          >{{ item.label }}
                        </div>
                      </template>
                      <template #item="{ item }">
                        <div class="d-flex align-center">
                          <span :style="dotStyle(item.value)"></span
                          >{{ item.label }}
                        </div>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </template>

              <!-- Sections (locked titles, editable bullets + symbols) -->
              <v-card
                v-for="(section, si) in form.sections"
                :key="section.key"
                outlined
                class="mb-4"
              >
                <div
                  class="section-header px-4 py-2 white--text font-weight-medium"
                  :style="{ background: typeColor }"
                >
                  {{ si + 1 }}. {{ section.title }}
                </div>
                <div class="pa-4">
                  <div
                    v-for="(bullet, bi) in section.bullets"
                    :key="bi"
                    class="d-flex align-center mb-2"
                  >
                    <v-icon small class="mr-2">mdi-circle-small</v-icon>
                    <v-text-field
                      v-model="section.bullets[bi]"
                      dense
                      hide-details
                      outlined
                      class="flex-grow-1"
                    />
                    <v-btn
                      icon
                      small
                      color="error"
                      class="ml-1"
                      @click="removeBullet(si, bi)"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                  <v-btn text small color="primary" @click="addBullet(si)">
                    <v-icon small left>mdi-plus</v-icon>
                    {{ $t("betriebsanweisung.addBullet") }}
                  </v-btn>

                  <!-- Symbols for this section -->
                  <v-divider class="my-3" />
                  <div class="d-flex align-center flex-wrap">
                    <span class="caption mr-3"
                      >{{ $t("betriebsanweisung.symbols") }}:</span
                    >
                    <div
                      v-for="key in section.symbols"
                      :key="key"
                      class="symbol-chip mr-2 mb-1"
                      :title="symbolLabel(key)"
                    >
                      <img
                        v-if="symbolFile(key)"
                        :src="symbolFile(key)"
                        :alt="symbolLabel(key)"
                        @error="onSymbolImgError"
                      />
                      <span v-else class="symbol-chip-fallback">
                        {{ symbolLabel(key) }}
                      </span>
                    </div>
                    <v-btn text small color="primary" @click="openSymbols(si)">
                      <v-icon small left>mdi-shape-plus</v-icon>
                      {{ $t("betriebsanweisung.chooseSymbols") }}
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </v-container>
          </v-card-text>

          <v-card-actions>
            <div class="flex-grow-1"></div>
            <v-btn color="grey" outlined @click="closeForm">
              {{ $t("general.back") }}
            </v-btn>
            <v-btn color="primary" :loading="saving" @click="saveForm">
              <span v-if="mode === 'edit'">{{
                $t("general.saveChanges")
              }}</span>
              <span v-else>{{ $t("general.save") }}</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col cols="12" sm="12" md="1"></v-col>
    </v-row>

    <v-dialog v-model="templateDialog.open" max-width="640" scrollable>
      <v-card>
        <v-card-title class="headline d-flex align-center flex-nowrap">
          <v-btn
            v-if="templateDialog.folder"
            icon
            class="mr-2"
            @click="templateDialog.folder = null"
          >
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="text-truncate">
            {{
              templateDialog.folder
                ? templateDialog.folder.name
                : $t("betriebsanweisung.chooseTemplate")
            }}
          </span>
        </v-card-title>
        <v-card-text>
          <div
            v-if="!templateDialog.templates.length"
            class="text--secondary py-4"
          >
            {{ $t("betriebsanweisung.noTemplates") }}
          </div>
          <v-list v-else-if="!templateDialog.folder">
            <v-list-item
              v-for="folder in templateFolderRows"
              :key="folder.id"
              @click="templateDialog.folder = folder"
            >
              <v-list-item-icon class="mr-4">
                <v-icon color="primary">
                  {{ folder.isNone ? "mdi-folder-outline" : "mdi-folder" }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ folder.name }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="flex-row align-center">
                <span class="grey--text mr-2">{{ folder.templates.length }}</span>
                <v-icon>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-list v-else dense>
            <v-list-item
              v-for="tpl in templateDialog.folder.templates"
              :key="tpl._id"
              @click="applyTemplate(tpl)"
            >
              <v-list-item-icon>
                <v-icon :color="baTypeColor(tpl.type)">{{
                  typeIcon(tpl.type)
                }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ tpl.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t(baTypeLabelKey(tpl.type)) }} ·
                  {{
                    tpl.scope === "global"
                      ? $t("betriebsanweisung.global")
                      : $t("betriebsanweisung.local")
                  }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn text @click="templateDialog.open = false">
            {{ $t("general.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Symbol picker dialog -->
    <v-dialog v-model="symbolDialog.open" max-width="720">
      <v-card>
        <v-card-title class="headline">
          {{ $t("betriebsanweisung.chooseSymbols") }}
        </v-card-title>
        <v-card-text>
          <safetySymbolPicker v-model="symbolDialog.selected" />
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn text @click="symbolDialog.open = false">
            {{ $t("general.cancel") }}
          </v-btn>
          <v-btn color="primary" @click="applySymbols">
            {{ $t("general.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { isMaster } from "../../utils/masterEmails";
import {
  BA_TYPES,
  baTypeConfig,
  defaultSectionsForType,
} from "../../data/betriebsanweisungTypes";
import { symbolByKey } from "../../data/safetySymbols";
import safetySymbolPicker from "../../components/_utils/safetySymbolPicker.vue";

function getDefaultForm(category) {
  return {
    type: "machine",
    title: "",
    number: "",
    company: "",
    workplace: "",
    responsiblePerson: "",
    tag: "",
    color: "",
    labelId: null,
    sourceTemplate: null,
    scope: "local",
    category: null,
    sections: defaultSectionsForType("machine"),
    instructionCategory: category || null,
  };
}

export default {
  name: "betriebsanweisungForm",
  components: { safetySymbolPicker },
  data() {
    return {
      baTypes: BA_TYPES,
      form: getDefaultForm(this.$route.params.instructionCategoryId),
      saving: false,
      isMaster: false,
      symbolDialog: { open: false, index: null, selected: [] },
      templateDialog: { open: false, templates: [], folders: [], folder: null },
      colorOptions: [
        { label: "Red", value: "red" },
        { label: "Green", value: "green" },
        { label: "Yellow", value: "gold" },
        { label: "Blue", value: "deepskyblue" },
        { label: "Orange", value: "orange" },
        { label: "Purple", value: "purple" },
      ],
      labels: [],
    };
  },
  computed: {
    templateFolderRows() {
      const byFolder = {};
      for (const tpl of this.templateDialog.templates) {
        const category = tpl.category;
        const key = category ? String(category._id || category) : "none";
        (byFolder[key] = byFolder[key] || []).push(tpl);
      }
      const rows = this.templateDialog.folders
        .map((folder) => ({
          id: String(folder._id),
          name: folder.name,
          isNone: false,
          templates: byFolder[String(folder._id)] || [],
        }))
        .filter((row) => row.templates.length)
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        );
      if (byFolder.none && byFolder.none.length) {
        rows.push({
          id: "none",
          name: this.$t("betriebsanweisung.folder.ohneOrdner"),
          isNone: true,
          templates: byFolder.none,
        });
      }
      return rows;
    },
    mode() {
      return this.$route.params.mode;
    },
    // The same component edits templates (no company-specific fields).
    isTemplate() {
      return this.$route.name === "betriebsanweisungTemplateForm";
    },
    typeColor() {
      return baTypeConfig(this.form.type).color;
    },
    titleFieldLabel() {
      return this.isTemplate
        ? this.$t("betriebsanweisung.templateName")
        : this.$t("betriebsanweisung.title");
    },
    scopeItems() {
      return [
        { text: this.$t("betriebsanweisung.local"), value: "local" },
        { text: this.$t("betriebsanweisung.global"), value: "global" },
      ];
    },
    labelTagNames() {
      return this.labels.map((l) => l.tag);
    },
    title() {
      if (this.isTemplate) {
        return this.mode === "edit"
          ? this.$t("betriebsanweisung.templateEdit")
          : this.$t("betriebsanweisung.templateNew");
      }
      if (this.mode === "edit") return this.$t("betriebsanweisung.edit");
      if (this.mode === "copy") return this.$t("betriebsanweisung.copy");
      return this.$t("betriebsanweisung.new");
    },
  },
  async created() {
    const { mode, id } = this.$route.params;

    const user = await this.$store.dispatch("getUserInfo");
    this.isMaster = user && isMaster(user.email);

    if (!this.isTemplate) await this.fetchLabels();

    // Remember the company: prefill a new document with the company entered on
    // the most recently created Betriebsanweisung (typed once, reused, still
    // editable). Not derived from the account name (accounts can differ).
    if (!this.isTemplate && this.mode === "new") {
      const res = await this.$store.dispatch("getBetriebsanweisungen");
      const list = (res && res.data) || [];
      const last = list
        .filter((d) => d.company)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
      if (last) this.form.company = last.company;

      if (this.$route.query.templateId) {
        const tpl = await this.$store.dispatch(
          "getSingleBetriebsanweisungTemplate",
          this.$route.query.templateId
        );
        if (tpl) this.applyTemplate(tpl);
      }
    }

    if (this.isTemplate) {
      if (mode === "new") {
        this.form.category = this.$route.params.templateCategoryId || null;
      }
      if (mode === "edit" || mode === "copy") {
        const tpl = await this.$store.dispatch(
          "getSingleBetriebsanweisungTemplate",
          id
        );
        if (tpl) {
          this.form = {
            ...getDefaultForm(null),
            _id: mode === "edit" ? tpl._id : undefined,
            type: tpl.type || "machine",
            title: tpl.name || "",
            scope: tpl.scope || "local",
            category: tpl.category || null,
            sections: this.cloneSections(tpl.sections),
          };
        }
      }
      return;
    }

    if (mode === "edit" || mode === "copy") {
      const item = await this.$store.dispatch("getSingleBetriebsanweisung", id);
      if (item) {
        const label =
          item.operatingInstructionLabel &&
          typeof item.operatingInstructionLabel === "object"
            ? item.operatingInstructionLabel
            : null;
        this.form = {
          ...getDefaultForm(this.$route.params.instructionCategoryId),
          ...item,
          tag: label ? label.tag || "" : "",
          color: label ? label.color || "" : "",
          labelId: label ? label._id : item.operatingInstructionLabel || null,
          instructionCategory:
            item.instructionCategory?._id ||
            item.instructionCategory ||
            this.$route.params.instructionCategoryId,
        };
        if (mode === "copy") {
          delete this.form._id;
          delete this.form.id;
          delete this.form.createdAt;
          delete this.form.updatedAt;
        }
      }
    }
  },
  methods: {
    typeIcon(type) {
      return {
        machine: "mdi-cog",
        dangerousGoods: "mdi-flask",
        biological: "mdi-biohazard",
      }[type];
    },
    typeCardStyle(t) {
      const active = this.form.type === t.value;
      return {
        cursor: "pointer",
        borderColor: active ? t.color : "rgba(0,0,0,0.12)",
        borderWidth: active ? "2px" : "1px",
        color: active ? t.color : "inherit",
      };
    },
    onTypeChange(type) {
      // Reseed locked section titles, but preserve content for sections whose
      // key exists in both the old and new type.
      const byKey = {};
      this.form.sections.forEach((s) => (byKey[s.key] = s));
      this.form.type = type;
      this.form.sections = defaultSectionsForType(type).map((d) =>
        byKey[d.key]
          ? {
              ...d,
              bullets: byKey[d.key].bullets,
              symbols: byKey[d.key].symbols,
            }
          : d
      );
    },
    addBullet(si) {
      this.form.sections[si].bullets.push("");
    },
    removeBullet(si, bi) {
      this.form.sections[si].bullets.splice(bi, 1);
    },
    symbolFile(key) {
      const s = symbolByKey(key);
      return s ? s.file : null;
    },
    symbolLabel(key) {
      const s = symbolByKey(key);
      return s ? this.$t(s.labelKey) : key;
    },
    onSymbolImgError(e) {
      e.target.style.display = "none";
    },
    openSymbols(si) {
      this.symbolDialog.index = si;
      this.symbolDialog.selected = [...(this.form.sections[si].symbols || [])];
      this.symbolDialog.open = true;
    },
    applySymbols() {
      const si = this.symbolDialog.index;
      if (si != null) {
        this.form.sections[si].symbols = [...this.symbolDialog.selected];
      }
      this.symbolDialog.open = false;
    },
    baTypeColor(type) {
      return baTypeConfig(type).color;
    },
    baTypeLabelKey(type) {
      return baTypeConfig(type).labelKey;
    },
    cloneSections(sections) {
      return (sections || []).map((s) => ({
        key: s.key,
        title: s.title,
        bullets: s.bullets && s.bullets.length ? [...s.bullets] : [""],
        symbols: [...(s.symbols || [])],
      }));
    },
    cleanSections() {
      return this.form.sections.map((s) => ({
        key: s.key,
        title: s.title,
        bullets: (s.bullets || []).filter((b) => (b || "").trim()),
        symbols: s.symbols || [],
      }));
    },
    async openTemplateChooser() {
      const [res, foldersRes] = await Promise.all([
        this.$store.dispatch("getBetriebsanweisungTemplates"),
        this.$store.dispatch("getBetriebsanweisungTemplateCategories"),
      ]);
      this.templateDialog.templates = (res && res.data) || [];
      this.templateDialog.folders = (foldersRes && foldersRes.data) || [];
      this.templateDialog.folder = null;
      this.templateDialog.open = true;
    },
    applyTemplate(tpl) {
      this.form.type = tpl.type || "machine";
      if (!this.form.title) this.form.title = tpl.name || "";
      this.form.sections = this.cloneSections(tpl.sections);
      this.form.sourceTemplate = tpl._id || tpl.id || null;
      this.templateDialog.open = false;
    },
    async fetchLabels() {
      const res = await this.$store.dispatch("getOperatingInstructionLabels");
      this.labels = (res && res.data) || (Array.isArray(res) ? res : []);
    },
    dotStyle(c) {
      return {
        display: "inline-block",
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        marginRight: "8px",
        backgroundColor: c || "transparent",
      };
    },
    colorForTag(tag) {
      const label = this.labels.find((l) => l.tag === tag);
      return label ? label.color : "";
    },
    onTagChange(val) {
      const label = this.labels.find((l) => l.tag === val);
      if (label) {
        this.form.color = label.color || "";
        this.form.labelId = label._id || null;
      } else {
        this.form.labelId = null;
      }
    },
    async resolveLabelId() {
      if (!this.form.tag || !this.form.color) return null;
      if (this.form.labelId) return this.form.labelId;
      if (this.form.tag && this.form.color) {
        const res = await this.$store.dispatch(
          "createOperatingInstructionLabel",
          { tag: this.form.tag, color: this.form.color }
        );
        return res ? res._id : null;
      }
      return null;
    },
    closeForm() {
      this.$router.back();
    },
    async saveForm() {
      if (!this.form.title) {
        this.$store.commit(
          "CREATE_SNACKBAR",
          this.$t("general.fieldNeedsToBeFilled")
        );
        return;
      }
      this.saving = true;
      try {
        if (this.isTemplate) {
          const tplPayload = {
            name: this.form.title,
            type: this.form.type,
            scope: this.isMaster ? this.form.scope : "local",
            category: this.form.category || null,
            sections: this.cleanSections(),
          };
          const saved =
            this.mode === "edit"
              ? await this.$store.dispatch("updateBetriebsanweisungTemplate", {
                  ...tplPayload,
                  id: this.form._id || this.$route.params.id,
                })
              : await this.$store.dispatch(
                  "createBetriebsanweisungTemplate",
                  tplPayload
                );
          if (!saved) return;
          this.closeForm();
          return;
        }

        const labelId = await this.resolveLabelId();
        const payload = {
          type: this.form.type,
          title: this.form.title,
          number: this.form.number,
          company: this.form.company,
          workplace: this.form.workplace,
          responsiblePerson: this.form.responsiblePerson,
          operatingInstructionLabel: labelId,
          sourceTemplate: this.form.sourceTemplate || null,
          instructionCategory:
            this.form.instructionCategory ||
            this.$route.params.instructionCategoryId,
          sections: this.cleanSections(),
        };

        if (this.mode === "edit") {
          await this.$store.dispatch("updateBetriebsanweisung", {
            ...payload,
            id: this.form._id || this.form.id,
          });
        } else {
          await this.$store.dispatch("createBetriebsanweisung", payload);
        }
        this.closeForm();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.type-card {
  display: inline-flex;
  align-items: center;
  min-width: 150px;
}
.section-header {
  font-size: 0.95rem;
}
.symbol-chip img {
  height: 34px;
  width: 34px;
  object-fit: contain;
}
.symbol-chip-fallback {
  display: inline-block;
  font-size: 0.6rem;
  max-width: 60px;
  line-height: 1.1;
  border: 1px dashed rgba(0, 0, 0, 0.3);
  padding: 2px 4px;
  border-radius: 4px;
}
</style>
